import React, { CSSProperties, forwardRef, useRef, useState } from "react";
import MuiTextField, {
	TextFieldProps as MuiTextFieldProps
} from "@material-ui/core/TextField";
import MuiFormControl, {
	FormControlProps as MuiFormControlProps
} from "@material-ui/core/FormControl";
import { makeStyles } from "@material-ui/core/styles";
import { DployFieldName } from "@ploy-lib/types";
import { Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";

const useLabelStyles = makeStyles(
	{
		root: {
			whitespace: "break-spaces",
			position: "relative",
			top: 0,
			marginBottom: -16
		},
		shrink: {
			transform: "translate(0, -20px) !important"
		}
	},
	{ name: "DployTextFieldLabel" }
);

const useFormControlStyles = makeStyles(
	{
		marginDense: {
			marginTop: 16
		},
		marginNormal: {}
	},
	{ name: "DployFormControl" }
);

const useInputStyles = makeStyles(
	{
		inputVariantOutline: {
			borderRadius: "0px"
		},
		selectVariantOutline: {
			borderRadius: 0,
			borderTop: "none",
			borderLeft: "none",
			borderRight: "none"
		},
		noBorderVariantOutline: {
			borderStyle: "none"
		},
		multiline: {
			padding: 0
		},
		input: (props: DployTextFieldProps) =>
			typeof props.textAlign === "string"
				? {
						textAlign: props.textAlign as CSSProperties["textAlign"]
				  }
				: {},
		square: {
			borderStyle: "none"
		},
		inputSquare: (props: DployTextFieldProps) => ({
			height: "100%",
			width: props.inputSquareWidth ?? "100%",
			resize: props.fullWidth ? "vertical" : "both",
			borderStyle: "solid",
			borderWidth: "0.933px",
			fontFamily: "Arial,Helvetica Neue,Helvetica,Roboto,sans-serif !important",
			fontSize: "11.5px",
			borderColor: "#a59bbf",
			minHeight: props.inputSquareMinHeight
		}),
		inputSquareOverridable: {},
		inputWrapperWithMaxLengthCaption: {
			flexDirection: "column",
			width: "100%"
		},
		maxLengthCaption: {
			display: "block",
			marginBottom: 8
		}
	},
	{ name: "DployInput" }
);

const dployVariants = ["select", "input", "noBorder"] as const;
const muiVariants = ["standard", "outlined", "filled"] as const;

type ValidDployVariants =
	| typeof muiVariants[number]
	| typeof dployVariants[number];

export type DployTextFieldProps = Omit<MuiTextFieldProps, "variant"> & {
	fieldName?: DployFieldName;
	locked?: boolean;
	variant?: ValidDployVariants;
	textAlign?: string;
	square?: boolean;
	maxLength?: number;
	inputSquareMinHeight?: number;
	inputSquareWidth?: number;
};

export const DployTextFieldInternal = (
	props: DployTextFieldProps,
	ref: React.Ref<any>
) => {
	const labelClasses = useLabelStyles(props);
	const inputClasses = useInputStyles(props);

	const { textAlign, maxLength, value, square, ...rest } = props;

	const [inputLength, setInputLength] = useState<number>(
		(value as string | undefined)?.length ?? 0
	);
	const initialFieldValue = useRef(value);

	const fieldValue = props.locked ? initialFieldValue.current : value;

	if (
		rest.variant &&
		muiVariants.includes(rest.variant as typeof muiVariants[number])
	) {
		const variant = rest.variant as typeof muiVariants[number];
		return (
			<MuiTextField ref={ref} {...rest} value={fieldValue} variant={variant} />
		);
	}

	const { variant = "input" } = props;
	const variantType = variant + "VariantOutline";

	const handleChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const rawValue = event.target.value;
		const value = maxLength ? rawValue.slice(0, maxLength) : rawValue;

		event.target.value = value;

		setInputLength(value.length);
		if (props.onChange) {
			props.onChange(event);
		}
	};

	const TextField = (
		<MuiTextField
			{...rest}
			ref={ref}
			value={fieldValue}
			autoComplete={props.autoComplete || "off"}
			variant="outlined"
			InputProps={{
				...props.InputProps,
				notched: false,
				classes: {
					input: `${inputClasses.input} ${
						square
							? `${inputClasses.inputSquare} ${inputClasses.inputSquareOverridable}`
							: ""
					}`,
					multiline: inputClasses.multiline,
					...(props.InputProps && props.InputProps.classes),
					notchedOutline: `${square ? inputClasses.square : ""} ${
						inputClasses[variantType]
					}`
				}
			}}
			onChange={handleChange}
			minRows={rest.rows}
			maxRows={rest.rows}
			InputLabelProps={{
				shrink: true,
				classes: labelClasses,
				...props.InputLabelProps
			}}
		/>
	);

	return maxLength && props.multiline ? (
		<div className={inputClasses.inputWrapperWithMaxLengthCaption}>
			{TextField}
			<Typography variant="caption" className={inputClasses.maxLengthCaption}>
				<FormattedMessage
					id="form_fields.dployTextField.maxCharacterLength"
					defaultMessage="{remaining} / {maxLength} characters"
					values={{
						remaining: maxLength - inputLength,
						maxLength: maxLength
					}}
				/>
			</Typography>
		</div>
	) : (
		TextField
	);
};

DployTextFieldInternal.displayName = "DployTextField";

const DployTextField = forwardRef(DployTextFieldInternal);

export type DployFormControlProps = MuiFormControlProps & {
	variant?: ValidDployVariants;
	textAlign?: string;
};

const DployFormControlInternal = (
	props: DployFormControlProps,
	ref: React.Ref<HTMLDivElement>
) => {
	const formControlClasses = useFormControlStyles(props);

	const { textAlign, ...rest } = props;

	if (rest.variant && muiVariants.includes(rest.variant))
		return <MuiFormControl variant="standard" ref={ref} {...rest} />;

	return (
		<MuiFormControl
			{...rest}
			ref={ref}
			variant="outlined"
			classes={formControlClasses}
		/>
	);
};

DployFormControlInternal.displayName = "DployFormControl";

const DployFormControl = forwardRef(DployFormControlInternal);

export { DployTextField, DployFormControl };
